import React, { useEffect, useState, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  fetchAdminReports,
  fetchAdminReportStats,
  badgeStatus,
  formatIsoDate,
  REPORT_CATEGORIES,
} from '../../lib/reportsAdminApi';
import { usePortalT } from '../../i18n/usePortalT';
import {
  PageTitle,
  PageSubtitle,
  Btn,
  BtnRow,
  Card,
  Table,
  Badge,
  ErrorText,
  StatGrid,
  StatCard,
  StatLabel,
  StatValue,
  Field,
} from '../../styles/adminStyles';

const STATUS_VALUES = ['pending', 'all', 'confirmed', 'rejected', 'awaiting_evidence', 'awaiting_response'];

export default function ReportListPage() {
  const { t, tEnum, language } = usePortalT();
  const [reports, setReports] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [status, setStatus] = useState('pending');
  const [category, setCategory] = useState('all');
  const [q, setQ] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [rows, s] = await Promise.all([
        fetchAdminReports({ status, category, q }),
        fetchAdminReportStats().catch(() => null),
      ]);
      setReports(rows);
      setStats(s);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Load failed');
    } finally {
      setLoading(false);
    }
  }, [status, category, q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div>
      <PageTitle>{t('reports.title')}</PageTitle>
      <PageSubtitle>{t('reports.subtitle')}</PageSubtitle>

      <StatGrid>
        <StatCard>
          <StatLabel>{t('reports.pending')}</StatLabel>
          <StatValue>{stats?.pending ?? '—'}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>{t('reports.penalized')}</StatLabel>
          <StatValue>{stats?.confirmed ?? '—'}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>{t('reports.pendingAppeals')}</StatLabel>
          <StatValue>{stats?.pendingAppeals ?? '—'}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>{t('reports.bannedUsers')}</StatLabel>
          <StatValue>{stats?.bannedUsers ?? '—'}</StatValue>
        </StatCard>
        <StatCard>
          <StatLabel>{t('reports.total')}</StatLabel>
          <StatValue>{stats?.total ?? '—'}</StatValue>
        </StatCard>
      </StatGrid>

      <Card style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', alignItems: 'flex-end' }}>
          <Field style={{ marginBottom: 0, minWidth: 150 }}>
            {t('reports.filterStatus')}
            <select value={status} onChange={(e) => setStatus(e.target.value)}>
              {STATUS_VALUES.map((v) => (
                <option key={v} value={v}>{tEnum('reportStatusFilter', v)}</option>
              ))}
            </select>
          </Field>
          <Field style={{ marginBottom: 0, minWidth: 150 }}>
            {t('reports.filterCategory')}
            <select value={category} onChange={(e) => setCategory(e.target.value)}>
              <option value="all">{t('reports.allCategories')}</option>
              {REPORT_CATEGORIES.map((c) => (
                <option key={c.value} value={c.value}>{tEnum('reportCategory', c.value)}</option>
              ))}
            </select>
          </Field>
          <Field style={{ marginBottom: 0, minWidth: 220, flex: 1 }}>
            {t('reports.searchLabel')}
            <input
              value={q}
              onChange={(e) => setQ(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
              placeholder={t('reports.searchPh')}
            />
          </Field>
          <Btn $primary type="button" onClick={load}>{t('common.query')}</Btn>
        </div>
      </Card>

      <BtnRow style={{ marginTop: 0, marginBottom: 16 }}>
        <Link to="/admin/appeals"><Btn>{t('reports.appealsQueue')}{stats?.pendingAppeals ? ` (${stats.pendingAppeals})` : ''}</Btn></Link>
        <Btn type="button" onClick={load}>{t('common.refresh')}</Btn>
      </BtnRow>

      {error ? <ErrorText>{error}</ErrorText> : null}
      {loading ? <p>{t('common.loading')}</p> : null}

      {!loading && reports.length === 0 ? (
        <Card>{t('reports.emptyFiltered')}</Card>
      ) : null}

      {!loading && reports.length > 0 ? (
        <Card style={{ padding: 0, overflow: 'hidden' }}>
          <Table>
            <thead>
              <tr>
                <th>{t('reports.colCategory')}</th>
                <th>{t('reports.colTarget')}</th>
                <th>{t('reports.colReporter')}</th>
                <th>{t('common.status')}</th>
                <th>{t('reports.colCredit')}</th>
                <th>{t('reports.colTime')}</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {reports.map((r) => (
                <tr key={r.id}>
                  <td>
                    <div style={{ fontWeight: 800 }}>{tEnum('reportCategory', r.category)}</div>
                    {r.description ? (
                      <div style={{ fontSize: 12, color: '#64748b', maxWidth: 260, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.description}
                      </div>
                    ) : null}
                  </td>
                  <td>
                    <div style={{ fontWeight: 700 }}>{r.targetName}</div>
                    {r.targetBannedUntil ? (
                      <div style={{ fontSize: 11, color: '#b91c1c', fontWeight: 700 }}>{t('reports.banned')}</div>
                    ) : null}
                  </td>
                  <td style={{ fontSize: 13 }}>{r.reporterName}</td>
                  <td><Badge $status={badgeStatus(r.status)}>{tEnum('reportStatus', r.status)}</Badge></td>
                  <td>{r.targetCreditScore ?? '—'}</td>
                  <td style={{ fontSize: 12 }}>{formatIsoDate(r.createdAt, language)}</td>
                  <td>
                    <Link to={`/admin/reports/${r.id}`}>{t('reports.handle')}</Link>
                    {r.pendingAppealCount > 0 ? (
                      <span style={{ marginLeft: 8, color: '#b45309', fontWeight: 800, fontSize: 12 }}>{t('reports.appeal')}</span>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        </Card>
      ) : null}
    </div>
  );
}
